"use client";

import React from "react";
import { Phone, MapPin, Clock, CircleCheck, Loader, Leaf } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const SERVICE_OPTIONS = ["Lawn Maintenance", "Mulching", "Landscape Design", "Tree and Shrub Care", "Spring and Fall Cleanups", "Patios", "Retaining Walls", "Snow Removal"];
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-foreground">Estimate Requested!</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6">
          Send Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} />
              </FormControl>
              <FormDescription>First and last name</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[0-9\-\+\s\(\)]+$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(412) 555-1234" {...field} />
              </FormControl>
              <FormDescription>Best number to reach you</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email address is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} />
            </FormControl>
            <FormDescription>We'll use this to get back to you</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" render={({
      field
    }) => <FormItem>
            <FormLabel>Service Interested In</FormLabel>
            <FormControl>
              <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-input/30">
                <option value="" disabled>
                  Select a service
                </option>
                {SERVICE_OPTIONS.map((service, i) => <option key={service} value={service} data-index={i}>
                    {service}
                  </option>)}
              </select>
            </FormControl>
            <FormDescription>Choose the service you'd like a free estimate for</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel>Additional Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your project or any special requests..." className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormDescription>Optional but helps us prepare a more accurate estimate</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-accent text-accent-foreground hover:bg-accent/90 shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Sending Request...
          </> : "Request a Free Estimate"}
      </Button>
    </Form>;
}
export default function LetsTalkLandscaping() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: infoRef,
    isVisible: isInfoVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="lets-talk-landscaping" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30 dark:opacity-10">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-accent/5 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-12 md:mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-4">
            <Leaf className="size-5 text-primary mr-2" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider pr-2">
              Get Started
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-foreground mb-4">
            Let's Talk Landscaping
          </h2>
          <p className="text-lg text-muted-foreground">
            Ready to transform your outdoor space? Fill out the form below for a free, no-obligation estimate, or give us a call directly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 items-start">
          {}
          <div ref={formRef} className={`lg:col-span-2 transition-all duration-700 delay-100 transform ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="border-border/50 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-4 border-b border-border/50 mb-6">
                <CardTitle className="text-2xl font-serif">Request an Estimate</CardTitle>
                <CardDescription className="text-base">
                  Provide your details and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formId="contact_form_get_an_estimate" sectionName="lets-talk-landscaping" defaultValues={{
                name: "",
                email: "",
                phone: "",
                service_interest: "",
                message: ""
              }} submitText="Request a Free Estimate" successMessage="Thank you for reaching out! We have received your request and will contact you shortly to discuss your landscaping needs.">
                  <ContactFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div ref={infoRef} className={`space-y-6 transition-all duration-700 delay-200 transform ${isInfoVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Card className="bg-primary text-primary-foreground border-none shadow-xl overflow-hidden relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-bl-full -mr-8 -mt-8 pointer-events-none" />
              <CardContent className="p-8 relative z-10">
                <h3 className="text-2xl font-serif font-bold mb-2">Need Immediate Assistance?</h3>
                <p className="text-primary-foreground/80 mb-8">
                  Skip the form and speak directly with our landscaping experts today.
                </p>
                
                <Button asChild size="lg" className="w-full h-14 text-lg bg-white text-primary hover:bg-white/90 shadow-md mb-8 group">
                  <Link to="tel:4127588781">
                    <Phone className="mr-2 size-5 group-hover:animate-pulse" />
                    Call (412) 758-8781
                  </Link>
                </Button>

                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-white/10 p-2 rounded-md shrink-0">
                      <MapPin className="size-5 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Service Area</h4>
                      <p className="text-primary-foreground/80 text-sm mt-1">
                        2746 Leechburg Rd<br />
                        Pittsburgh, PA 15239<br />
                        Serving Pittsburgh and nearby communities.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="bg-white/10 p-2 rounded-md shrink-0">
                      <Clock className="size-5 text-accent" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white">Business Hours</h4>
                      <p className="text-primary-foreground/80 text-sm mt-1">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 2:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-muted/50 rounded-xl p-6 border border-border/50">
              <h4 className="font-semibold text-foreground mb-3 flex items-center">
                <CircleCheck className="size-4 text-primary mr-2" />
                Why Choose Us?
              </h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start">
                  <span className="mr-2 text-accent">•</span>
                  Over 40 years of local landscaping experience
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-accent">•</span>
                  Reliable, professional, and fully insured
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-accent">•</span>
                  Free, transparent estimates with no hidden fees
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}